import React, { Fragment, FunctionComponent, useContext, useEffect, useState } from 'react';

import { Text } from '../../../../components/shared/Text';
import { StepperContent, StepperHeader, StepperSubmit } from '../../../shared/Stepper';
import { FormContext, SurPlusResultData } from '../../../../store/surplus/SurPlusContext';
import { InputHolder, InputHolderItem } from '../../../shared/InputHolder';
import { Input } from '../../../shared/Input';
import { formItems } from '../formItems';
import { useForm } from 'react-hook-form';
import { currencyToNumber, numberToCurrency, scrollToTop } from '../../../../utils';
import { Radio } from '../../../shared/Radio';

export const Step3: FunctionComponent<{ id: number }> = ({id}) => {

	const {
		register,
		unregister,
		setValue,
		handleSubmit,
		formState: {errors, isValid},
	} = useForm({
		mode: 'onBlur'
	});

	const [completedText, setCompletedText] = useState('');
	const [isSelling, setIsSelling] = useState(true);
	const { updateForm, updateData, form } = useContext(FormContext);
	const notaryCosts = (isSelling) ? formItems.step3.notary.defaultValue.selling : formItems.step3.notary.defaultValue.remodeling;

	useEffect(() => {
		if (form[formItems.housePrice]) {
			const salesCosts: number = Math.round((parseInt(form[formItems.housePrice]) / 100) * 2);
			setValue('estateAgent', numberToCurrency(salesCosts, true), {
				shouldValidate: true
			});
			setValue('notary', notaryCosts , {
				shouldValidate: true
			});
		}
	},[form, setValue, isSelling, notaryCosts])

	useEffect(() => {
		if (!isSelling) {
			unregister(['estateAgent']);
		}

	}, [isSelling, unregister]);

	const calculateCosts = (data: { [key: string]: any }, form: { [key: string]: any }, isSelling: boolean) => {
		let costs = parseInt(data[formItems.step3.administration.name]) + parseInt(data[formItems.step3.notary.name]) + parseInt(data[formItems.step3.taxationCurrent.name]);
		if (isSelling) {
				costs += parseInt(data[formItems.step3.taxationNew.name]) + parseInt(data[formItems.step3.estateAgent.name]);
		}
		return costs;
	}


	const onSubmit = (data: any) => {
		data[formItems.step3.reasonForCalculation.name] = (isSelling) ? 'selling' : 'remodeling';
		data[formItems.step3.administration.name] = currencyToNumber(data[formItems.step3.administration.name]);
		data[formItems.step3.notary.name] = currencyToNumber(data[formItems.step3.notary.name]);
		data[formItems.step3.taxationCurrent.name] = currencyToNumber(data[formItems.step3.taxationCurrent.name]);
		if (isSelling) {
			data[formItems.step3.taxationNew.name] = currencyToNumber(data[formItems.step3.taxationNew.name]);
			data[formItems.step3.estateAgent.name] = currencyToNumber(data[formItems.step3.estateAgent.name]);
		}
		updateForm(data);
		const salesCosts: number = calculateCosts(data, form, isSelling);
		const surPlusResultData: SurPlusResultData = {
			surplus: parseInt(form[formItems.housePrice]) - salesCosts - parseInt(form[formItems.mortgage]),
			salesPrice: form[formItems.housePrice],
			salesCosts: salesCosts,
			mortgage: form[formItems.mortgage]
		}
		updateData(surPlusResultData)
		setCompletedText(numberToCurrency(salesCosts, true));
		scrollToTop();
	};

	return (
		<Fragment>
			<StepperHeader title="Bijkomende komsten" id={id} completedText={completedText}/>

			<StepperContent>
				<Text as="h3">{formItems.step3.reasonForCalculation.title}</Text>
				<form onSubmit={handleSubmit(onSubmit)}>

					<InputHolder isFlexWrap={true}>
						<InputHolderItem isFlexBasis={true}>
							<label className="input__label">
								{formItems.step3.reasonForCalculation.label}</label>
						</InputHolderItem>

						<InputHolderItem isSmall={false}>

							<Radio
								label={formItems.step3.reasonForCalculation.options.sell.label}
								checked={true}
								name={formItems.step3.reasonForCalculation.name}
								id={`${formItems.step3.reasonForCalculation.name}-1`}
								defaultValue={'true'}
								register={register}
								validation={{
									onChange: (e: any) =>
										setIsSelling(e.currentTarget.value === 'true'),
								}}
							/>
						</InputHolderItem>
						<InputHolderItem isSmall={false}>
							<Radio
								label={formItems.step3.reasonForCalculation.options.remodel.label}
								name={formItems.step3.reasonForCalculation.name}
								id={`${formItems.step3.reasonForCalculation.name}-2`}
								defaultValue={'false'}
								register={register}
							/>
						</InputHolderItem>
					</InputHolder>

					<InputHolder>
						<InputHolderItem isFullWidth={true}>
							<Input
								label={formItems.step3.administration.label}
								name={formItems.step3.administration.name}
								id={formItems.step3.administration.name}
								type="currency"
								hasError={errors[formItems.step3.administration.name]}
								placeholder="€"
								defaultValue={formItems.step3.administration.defaultValue}
								register={register}
								validation={{required: true}}
							/>
							{errors[formItems.step3.administration.name] && (
								<span className="form__error">{formItems.step3.administration.error}</span>
							)}
							<Text as="p">
								{formItems.step3.administration.tooltip}
							</Text>
						</InputHolderItem>
					</InputHolder>

					<InputHolder tooltip={formItems.step3.taxationCurrent.tooltip}>
						<InputHolderItem isFullWidth={true}>
							<Input
								label={formItems.step3.taxationCurrent.label}
								name={formItems.step3.taxationCurrent.name}
								id={formItems.step3.taxationCurrent.name}
								type="currency"
								hasError={errors[formItems.step3.taxationCurrent.name]}
								placeholder="€"
								defaultValue={formItems.step3.taxationCurrent.defaultValue}
								register={register}
								validation={{required: true}}
							/>
							{errors[formItems.step3.taxationCurrent.name] && (
								<span className="form__error">{formItems.step3.taxationCurrent.error}</span>
							)}
						</InputHolderItem>
					</InputHolder>

					{isSelling ? (
						<Fragment>
								<InputHolder tooltip={formItems.step3.taxationNew.tooltip}>
									<InputHolderItem isFullWidth={true}>
										<Input
											label={formItems.step3.taxationNew.label}
											name={formItems.step3.taxationNew.name}
											id={formItems.step3.taxationNew.name}
											type="currency"
											hasError={errors[formItems.step3.taxationNew.name]}
											placeholder="€"
											defaultValue={formItems.step3.taxationNew.defaultValue}
											register={register}
											validation={{required: true}}
										/>
										{errors[formItems.step3.taxationNew.name] && (
											<span className="form__error">{formItems.step3.taxationNew.error}</span>
										)}
									</InputHolderItem>
								</InputHolder>

							<InputHolder tooltip={formItems.step3.estateAgent.tooltip}>
								<InputHolderItem isFullWidth={true}>
									<Input
										label={formItems.step3.estateAgent.label}
										name={formItems.step3.estateAgent.name}
										id={formItems.step3.estateAgent.name}
										type="currency"
										hasError={errors[formItems.step3.estateAgent.name]}
										placeholder="€"
										register={register}
										validation={{required: true}}
									/>
									{errors[formItems.step3.estateAgent.name] && (
										<span className="form__error">{formItems.step3.estateAgent.error}</span>
									)}
								</InputHolderItem>
							</InputHolder>
						</Fragment>
					) : null}

					<InputHolder tooltip={formItems.step3.notary.tooltip.replace(/{notaryCosts}/g, notaryCosts)}>
						<InputHolderItem isFullWidth={true}>
							<Input
								label={formItems.step3.notary.label}
								name={formItems.step3.notary.name}
								id={formItems.step3.notary.name}
								type="currency"
								hasError={errors[formItems.step3.notary.name]}
								placeholder="€"
								register={register}
								validation={{required: true}}
							/>
							{errors[formItems.step3.notary.name] && (
								<span className="form__error">{formItems.step3.notary.error}</span>
							)}
						</InputHolderItem>
					</InputHolder>
					{isSelling ? (
						<Text as={'p'}>Eventuele andere bijkomende kosten zijn: NHG kosten (0,6% van de koopsom van de nieuwe woning) indien het geval de nieuwe hypotheek wordt afgesloten met NHG, kosten voor de aankoopmakelaar (circa € 2.000-€ 2.500). In de berekening is ook geen rekening gehouden met de overdrachtsbelasting. Dit bedraagt 2% van de koopsom van de nieuwe woning.</Text>
					) : null}
					<StepperSubmit id={id} isValidated={isValid}/>
				</form>
			</StepperContent>
		</Fragment>
	);
};
